import { z } from 'zod'
import type { Artifacts } from './types'

/**
 * THE runtime schema for {@link Artifacts} — the single source both for validating artifacts
 * reconstructed from untrusted storage (the overview DocumentReference JSON — see
 * clinical-summary/overview/get.ts) and for the API contracts (thread entries, the
 * clinical-summary details/overview endpoints), which import it. Dependency-clean (zod + type-only
 * imports), so contracts bundled into the web can pull it across the package boundary.
 *
 * STRICT about `group` being a path (Code[]): artifacts persisted before the path migration fail
 * validation — for overviews that reads as "no overview stored" and triggers a rebuild (a schema
 * change alters the details hash anyway); old threads simply fail, which is accepted. The drift
 * guard below keeps this schema in step with the `Artifacts` type.
 */

const codingSchema = z.object({
  code: z.string().nullable(),
  system: z.string().nullable(),
  display: z.string().nullable(),
})

const codeSchema = z.object({
  text: z.string().nullable(),
  coding: z.array(codingSchema),
})

const groupSpecShape = { group: z.array(codeSchema).nullable().optional() }

export const chartDatumSchema = z.looseObject({
  referenceLow: z.number().nullable().optional(),
  referenceHigh: z.number().nullable().optional(),
  abnormalFlag: z
    .enum(['high', 'low', 'critical-high', 'critical-low', 'abnormal'])
    .nullable()
    .optional(),
  comparator: z.enum(['<', '<=', '>=', '>']).nullable().optional(),
})

export const chartSpecSchema = z.object({
  ...groupSpecShape,
  chartId: z.string(),
  type: z.enum(['bar', 'line', 'pie', 'donut']),
  title: z.string(),
  subTitle: z.string().nullable().optional(),
  xKey: z.string(),
  yKey: z.string(),
  data: z.array(chartDatumSchema),
})

const tableSpecSchema = z.object({
  ...groupSpecShape,
  tableId: z.string(),
  title: z.string(),
  subTitle: z.string().nullable().optional(),
  columns: z.array(z.string()),
  rows: z.array(z.array(z.union([z.string(), z.number(), z.null()]))),
})

const kpiSpecSchema = z.object({
  ...groupSpecShape,
  kpiId: z.string(),
  label: z.string(),
  value: z.string(),
  trend: z.string().nullable().optional(),
  status: z.enum(['good', 'warning', 'critical', 'neutral']).optional(),
})

// The WIRE shape of a special is deliberately loose (`type` + passthrough):
// stored reports are long-lived unversioned JSON, so a strict union here would
// fail a whole endpoint response over one legacy/unknown special instead of
// degrading to "not rendered". Strictness lives at the single write point (the
// envelope parser) and in the per-type schemas (assistant's user-facing-entry
// contract), which consumers apply when narrowing — each pins its type's
// `version` (see the versioning rule there), so a reader never parses a special
// version it doesn't know. The static type is pinned to `{ type: string;
// version?: number }` (dropping looseObject's index signature) so the
// middleware's strict special interfaces stay assignable to handler outputs;
// runtime passthrough is unaffected.
export const artifactSpecialSchema: z.ZodType<{ type: string; version?: number }> = z.looseObject({
  type: z.string(),
  version: z.number().optional(),
})

export const artifactsSchema = z.object({
  answer: z.string().optional(),
  charts: z.array(chartSpecSchema).optional(),
  tables: z.array(tableSpecSchema).optional(),
  kpis: z.array(kpiSpecSchema).optional(),
  specials: z.array(artifactSpecialSchema).optional(),
  followUpSuggestions: z.array(z.string()).optional(),
  request: z.object({ entity: z.literal('patient'), mask: z.string().optional() }).optional(),
})

// Compile-time drift guard: the schema's inferred type and Artifacts must stay mutually assignable.
type AssertArtifactsSchemaMatches =
  z.infer<typeof artifactsSchema> extends Artifacts
    ? Artifacts extends z.infer<typeof artifactsSchema>
      ? true
      : never
    : never
const _assertArtifactsSchemaMatches: AssertArtifactsSchemaMatches = true
void _assertArtifactsSchemaMatches
